import { memo, PropsWithChildren } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Input } from '@ds';
import { InfoBanner } from '@ui';
import { useCurrentUser } from '@lp/core/hooks/useCurrentUser';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { FormRow } from '@features/components/CaseFormBlocks/components';
import { CaseStatus, OrganType } from '@/api/apis/case';
import {
  DonorTypeSelector,
  LateralitySelector,
  NopServiceSelector,
  NrpSelector,
  OrganTypeSelector,
  ServiceRegionSelector,
} from './components';

export type CaseGeneralProps = {
  disabled?: Partial<Record<keyof CaseFormType, boolean>>;
  showRequired?: boolean;
  hasPermission?: boolean;
  isDuplicate?: boolean;
  allowDirectAcquisition?: boolean;
  caseStatus?: CaseStatus;
  availableOrganTypes?: OrganType[];
  disallowLateralityUnset?: boolean;
};

export const CaseGeneral = memo(
  ({
    showRequired,
    disabled,
    children,
    hasPermission,
    isDuplicate,
    allowDirectAcquisition = false,
    caseStatus,
    availableOrganTypes,
    disallowLateralityUnset,
  }: CaseGeneralProps & PropsWithChildren) => {
    const { control } = useFormContext<CaseFormType>();
    const { data: user } = useCurrentUser();
    const currentOrganType = useWatch({ control, name: 'organType' });

    return (
      <>
        <FormRow title="Service Region" isRequired>
          <div className="grow-0 flex-[48.5%]">
            <ServiceRegionSelector isDisabled={disabled?.serviceRegionId || !hasPermission} />
          </div>
        </FormRow>
        <FormRow title="Donor ID" isRequired={showRequired}>
          <div className="grow-0 flex-[48.5%]">
            <Controller
              name="donorId"
              control={control}
              render={({ field, fieldState: { invalid, error } }) => (
                <Input
                  isDisabled={disabled?.donorId || !hasPermission}
                  placeholder="Enter Donor ID"
                  aria-label="Enter Donor ID"
                  type="text"
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  {...field}
                />
              )}
            />
          </div>
        </FormRow>
        <OrganTypeSelector
          isRequired={showRequired}
          disabled={disabled?.organType}
          hasPermission={hasPermission}
          caseStatus={caseStatus}
          availableOrganTypes={availableOrganTypes}
        />
        {currentOrganType === OrganType.Kidney && (
          <LateralitySelector
            disabled={disabled?.laterality}
            hasPermission={hasPermission}
            disallowUnset={disallowLateralityUnset}
          />
        )}
        {isDuplicate && (
          <div className="mt-2">
            <InfoBanner iconType="warning">
              The similar case identified in the system. Ensure that it is not a duplicate.
            </InfoBanner>
          </div>
        )}
        <DonorTypeSelector isRequired={showRequired} disabled={disabled?.donorType} hasPermission={hasPermission} />
        <NopServiceSelector
          isRequired={showRequired}
          disabled={disabled?.serviceType}
          detailsDisabled={disabled?.serviceDetails}
          hasPermission={hasPermission}
          allowDirectAcquisition={allowDirectAcquisition}
          employeeRoles={user?.employee?.roles}
          caseStatus={caseStatus}
        />
        {children}
        <NrpSelector control={control} disable={disabled?.nrp || !hasPermission} />
      </>
    );
  },
);
